if (!customElements.get('same-day-shipping-bar')) {
  class SameDayShippingBar extends HTMLElement {
    static testTime = null;

    static easternTimeParts(date = new Date()) {
      if (SameDayShippingBar.testTime) return SameDayShippingBar.testTime;
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
        hourCycle: 'h23'
      });
      const parts = formatter.formatToParts(date).reduce((values, part) => {
        values[part.type] = part.value;
        return values;
      }, {});

      return {
        weekday: parts.weekday,
        hour: Number(parts.hour),
        minute: Number(parts.minute)
      };
    }

    constructor() {
      super();

      this.cutoffHour = 17;
      this.text = this.querySelector('[data-same-day-shipping-text]');
      this.progress = this.querySelector('.same-day-shipping-progress');
      this.detailsTrigger = this.querySelector('[data-same-day-shipping-details-trigger]');
      this.tooltip = this.querySelector('.same-day-shipping-tooltip');
      this.intervalId = null;
      this.boundToggleTooltip = this.toggleTooltip.bind(this);
      this.boundShowTooltipOnHover = this.showTooltipOnHover.bind(this);
      this.boundHideTooltipOnHover = this.hideTooltipOnHover.bind(this);
      this.boundCloseTooltip = this.closeTooltip.bind(this);
    }

    connectedCallback() {
      this.updateBar();
      this.intervalId = window.setInterval(() => this.updateBar(), 60000);

      if (this.detailsTrigger && this.tooltip) {
        this.detailsTrigger.addEventListener('click', this.boundToggleTooltip);
        this.detailsTrigger.addEventListener('mouseenter', this.boundShowTooltipOnHover);
        this.addEventListener('mouseleave', this.boundHideTooltipOnHover);
        this.tooltip.addEventListener('mouseleave', this.boundHideTooltipOnHover);
        document.addEventListener('click', this.boundCloseTooltip);
      }
    }

    disconnectedCallback() {
      if (this.intervalId) window.clearInterval(this.intervalId);

      if (this.detailsTrigger) {
        this.detailsTrigger.removeEventListener('click', this.boundToggleTooltip);
        this.detailsTrigger.removeEventListener('mouseenter', this.boundShowTooltipOnHover);
      }
      this.removeEventListener('mouseleave', this.boundHideTooltipOnHover);
      if (this.tooltip) this.tooltip.removeEventListener('mouseleave', this.boundHideTooltipOnHover);
      document.removeEventListener('click', this.boundCloseTooltip);
    }

    toggleTooltip(event) {
      event.stopPropagation();
      if (!this.detailsTrigger || !this.tooltip) return;

      if (this.tooltip.hidden) {
        this.showTooltip();
      } else {
        this.hideTooltip();
      }
    }

    showTooltipOnHover() {
      this.showTooltip();
    }

    hideTooltipOnHover() {
      this.hideTooltip();
    }

    showTooltip() {
      if (!this.detailsTrigger || !this.tooltip) return;

      this.tooltip.hidden = false;
      this.detailsTrigger.setAttribute('aria-expanded', 'true');
    }

    hideTooltip() {
      if (!this.detailsTrigger || !this.tooltip) return;

      this.tooltip.hidden = true;
      this.detailsTrigger.setAttribute('aria-expanded', 'false');
    }

    closeTooltip(event) {
      if (!this.tooltip || this.tooltip.hidden || this.contains(event.target)) return;

      this.hideTooltip();
    }

    formatRemaining(minutesRemaining) {
      const hours = Math.floor(minutesRemaining / 60);
      const minutes = minutesRemaining % 60;

      if (hours <= 0) return `${minutes}m`;
      if (minutes <= 0) return `${hours}h`;
      return `${hours}h ${minutes}m`;
    }

    updateBar() {
      if (!this.text || !this.progress) return;

      const { weekday, hour, minute } = SameDayShippingBar.easternTimeParts();
      const currentMinutes = hour * 60 + minute;
      const cutoffMinutes = this.cutoffHour * 60;
      const minutesRemaining = cutoffMinutes - currentMinutes;
      const isSunday = weekday === 'Sun';
      const isSaturday = weekday === 'Sat';

      if (isSunday) {
        this.setMessage("We don't ship Sundays — order today and we'll ship your order out Monday!", 100, 'inactive');
        return;
      }

      if (minutesRemaining <= 0) {
        const message = isSaturday
          ? "Order now and we'll ship your order out Monday!"
          : "Order now and we'll ship your order out tomorrow!";
        this.setMessage(message, 100, 'inactive');
        return;
      }

      const progress = Math.max(0, Math.min(100, ((cutoffMinutes - minutesRemaining) / cutoffMinutes) * 100));

      if (minutesRemaining <= 30) {
        this.setMessage(
          `Last Chance - order in the next <strong>${this.formatRemaining(minutesRemaining)}</strong> for shipping by <strong>5pm ET</strong>.`,
          progress,
          'urgent'
        );
        return;
      }

      if (minutesRemaining <= 120) {
        this.setMessage(
          `Order in the next <strong>${this.formatRemaining(minutesRemaining)}</strong> for same-day shipping by <strong>5pm ET</strong>.`,
          progress,
          'warning'
        );
        return;
      }

      if (minutesRemaining > 480) {
        this.setMessage('Order today for same-day shipping by <strong>5pm ET</strong>.', progress, 'active');
        return;
      }

      this.setMessage(
        `Order in the next <strong>${this.formatRemaining(minutesRemaining)}</strong> for same-day shipping by <strong>5pm ET</strong>.`,
        progress,
        'active'
      );
    }

    setMessage(message, progress, status) {
      this.text.innerHTML = message;
      this.progress.style.setProperty('--progress-width', `${progress}%`);
      this.dataset.status = status;
    }
  }


  window.sameDayShippingBarTest = {
    set(weekday, hour, minute = 0) {
      const weekdayMap = {
        sunday: 'Sun',
        sun: 'Sun',
        monday: 'Mon',
        mon: 'Mon',
        tuesday: 'Tue',
        tue: 'Tue',
        wednesday: 'Wed',
        wed: 'Wed',
        thursday: 'Thu',
        thu: 'Thu',
        friday: 'Fri',
        fri: 'Fri',
        saturday: 'Sat',
        sat: 'Sat'
      };
      const normalizedWeekday = weekdayMap[String(weekday).toLowerCase()];
      const normalizedHour = Number(hour);
      const normalizedMinute = Number(minute);

      if (!normalizedWeekday || Number.isNaN(normalizedHour) || Number.isNaN(normalizedMinute)) {
        console.warn('Use: sameDayShippingBarTest.set("Mon", 14, 30)');
        return;
      }

      SameDayShippingBar.testTime = {
        weekday: normalizedWeekday,
        hour: Math.max(0, Math.min(23, normalizedHour)),
        minute: Math.max(0, Math.min(59, normalizedMinute))
      };
      this.refresh();
    },
    clear() {
      SameDayShippingBar.testTime = null;
      this.refresh();
    },
    refresh() {
      document.querySelectorAll('same-day-shipping-bar').forEach((bar) => bar.updateBar());
    }
  };


  customElements.define('same-day-shipping-bar', SameDayShippingBar);
}